import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackTitle?: string;
  fallback?: React.ComponentType<{ onRetry: () => void }>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    componentStack: null,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error in child tree:', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  private handleReport = async (): Promise<void> => {
    const { error, componentStack } = this.state;
    const details = [
      'eCliniq — Reporte de error',
      '',
      `Mensaje: ${error?.message ?? '(sin mensaje)'}`,
      `Origen: ${window.location.pathname}`,
      `Fecha: ${new Date().toISOString()}`,
      '',
      'Stack:',
      error?.stack ?? '(no disponible)',
      '',
      'Component stack:',
      componentStack ?? '(no disponible)',
    ].join('\n');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(details);
        window.alert('Los detalles del error fueron copiados al portapapeles. Pegue el contenido en el reporte a soporte.');
      } else {
        window.alert(details);
      }
    } catch {
      window.alert(details);
    }
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      const Fallback = this.props.fallback;
      return <Fallback onRetry={this.handleReset} />;
    }

    const title = this.props.fallbackTitle ?? 'Algo salió mal';

    return (
      <div className="container-fluid" role="alert" aria-live="assertive">
        <div className="row justify-content-center mt-5">
          <div className="col-md-8 col-lg-6">
            <div className="card border-danger">
              <div className="card-header bg-danger text-white d-flex align-items-center">
                <i className="fa fa-exclamation-triangle me-2" aria-hidden="true"></i>
                <h4 className="card-title mb-0">{title}</h4>
              </div>
              <div className="card-body">
                <p className="mb-3">
                  Ocurrió un error inesperado. Su trabajo no guardado se ha preservado en esta pestaña.
                  Por favor recargue la página o contacte al equipo de soporte si el problema persiste.
                </p>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={this.handleReload}
                  >
                    <i className="fa fa-refresh me-1" aria-hidden="true"></i>
                    Recargar página
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={this.handleReport}
                  >
                    <i className="fa fa-envelope me-1" aria-hidden="true"></i>
                    Reportar error
                  </button>
                </div>
                <details className="mt-3">
                  <summary className="text-muted" style={{ cursor: 'pointer' }}>
                    Detalles técnicos
                  </summary>
                  <pre
                    className="small mt-2 p-2 bg-light border rounded"
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
{this.state.error?.stack ?? '(sin stack disponible)'}
                  </pre>
                </details>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}